import { Form, Formik, type FormikHelpers } from "formik";
import { Building2, Check, Circle, Handshake, Plus, UserRound, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { canSkipOnboarding } from "@/features/auth/roles";
import { FormField } from "@/components/common/form-field";
import { EinField, SsnField } from "@/components/common/masked-form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createClient } from "@/features/clients/api";
import { MemberMultiSelect } from "@/features/clients/components/member-multi-select";
import { useFirmContext } from "@/features/clients/hooks/use-firm-context";
import type { ClientType, CreateClientPayload } from "@/features/clients/types";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { listMembers, type Member } from "@/features/team/api";
import { applyApiErrors } from "@/lib/form-errors";
import { EIN_RE, SSN_RE } from "@/lib/masks";
import { cn } from "@/lib/utils";

const MAX_PRACTITIONERS = 6;

/** Uppercase micro-label used across the data-entry grids (matches client profile). */
const FIELD_LABEL = "text-muted-foreground text-xs font-semibold tracking-wider uppercase";

interface AddClientValues {
  clientType: ClientType;
  // Individual
  firstName: string;
  middleInitial: string;
  lastName: string;
  ssnTin: string;
  email: string;
  // Business
  businessName: string;
  businessEmail: string;
  dba: string;
  ein: string;
  /** ISO alpha-2 country code, shared by both client types. */
  country: string;
  /** Slot 0 is the required primary; slots 1+ are optional additions. */
  practitioners: string[];
  /** Team members (user ids) assigned to this client — optional, unordered. */
  assignedMembers: string[];
  /** Create the client already active, skipping the New-Arrival pipeline. */
  skipOnboarding: boolean;
  /** "Refer by" affiliate (NSI house book only). */
  referrerName: string;
  referralCommissionPercent: string;
}

const baseInitialValues: Omit<AddClientValues, "practitioners" | "assignedMembers"> = {
  clientType: "Individual",
  firstName: "",
  middleInitial: "",
  lastName: "",
  ssnTin: "",
  email: "",
  businessName: "",
  businessEmail: "",
  dba: "",
  ein: "",
  country: "US",
  skipOnboarding: false,
  referrerName: "",
  referralCommissionPercent: "",
};

const addClientSchema = Yup.object({
  clientType: Yup.string().oneOf(["Individual", "Business"]).required(),
  firstName: Yup.string().when("clientType", {
    is: "Individual",
    then: (s) => s.required("First name is required"),
    otherwise: (s) => s.notRequired(),
  }),
  lastName: Yup.string().when("clientType", {
    is: "Individual",
    then: (s) => s.required("Last name is required"),
    otherwise: (s) => s.notRequired(),
  }),
  ssnTin: Yup.string().when("clientType", {
    is: "Individual",
    then: (s) => s.required("SSN/ITIN is required").matches(SSN_RE, "Enter a valid SSN/ITIN"),
    otherwise: (s) => s.notRequired(),
  }),
  email: Yup.string().email("Enter a valid email"),
  businessName: Yup.string().when("clientType", {
    is: "Business",
    then: (s) => s.required("Business name is required"),
    otherwise: (s) => s.notRequired(),
  }),
  businessEmail: Yup.string().email("Enter a valid email"),
  ein: Yup.string().when("clientType", {
    is: "Business",
    then: (s) => s.required("EIN is required").matches(EIN_RE, "Enter a valid EIN"),
    otherwise: (s) => s.notRequired(),
  }),
  practitioners: Yup.array()
    .of(Yup.string())
    .test("primary-required", "Select a primary practitioner", (arr) => Boolean(arr?.[0])),
  referralCommissionPercent: Yup.number()
    .transform((v, orig) => (orig === "" ? undefined : v))
    .typeError("Enter a number")
    .min(0, "Must be 0–100")
    .max(100, "Must be 0–100"),
});

const clientTypes: { value: ClientType; icon: typeof UserRound; title: string; hint: string }[] = [
  {
    value: "Individual",
    icon: UserRound,
    title: "Individual",
    hint: "Person filing under an SSN/ITIN",
  },
  { value: "Business", icon: Building2, title: "Business", hint: "Entity filing under an EIN" },
];

/** Section wrapper matching the client-profile card style: header band + body. */
function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof UserRound;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Icon className="text-primary size-5" /> {title}
        </h3>
      </div>
      {children}
    </Card>
  );
}

/**
 * New-client form. Formik owns state/submission, Yup owns validation, and
 * `FormField` owns presentation — mirroring `features/auth/login-form`.
 *
 * Sensitive fields (SSN/ITIN, EIN) live in Formik state only for the duration of
 * the form and are never logged or persisted — see the `sensitive-data-handling`
 * conventions in CLAUDE.md.
 */
/** Maps the Formik values onto the snake_case create-client API payload.
 * ``includeReferral`` is only true for the NSI house book — the backend rejects
 * referral fields for a Business-Practitioner client, so they're omitted there. */
function toPayload(values: AddClientValues, includeReferral: boolean): CreateClientPayload {
  const practitioner_ids = values.practitioners.filter(Boolean);
  const assigned_member_ids = values.assignedMembers;
  const skip_onboarding = values.skipOnboarding;
  const referral = includeReferral
    ? {
        referrer_name: values.referrerName,
        referral_commission_percent: values.referralCommissionPercent
          ? Number(values.referralCommissionPercent)
          : null,
      }
    : {};
  if (values.clientType === "Individual") {
    return {
      client_type: "INDIVIDUAL",
      first_name: values.firstName,
      middle_initial: values.middleInitial,
      last_name: values.lastName,
      ssn_tin: values.ssnTin,
      email: values.email,
      country: values.country,
      practitioner_ids,
      assigned_member_ids,
      skip_onboarding,
      ...referral,
    };
  }
  return {
    client_type: "BUSINESS",
    business_name: values.businessName,
    dba: values.dba,
    ein: values.ein,
    email: values.businessEmail,
    country: values.country,
    practitioner_ids,
    assigned_member_ids,
    skip_onboarding,
    ...referral,
  };
}

export function AddClientForm() {
  const navigate = useNavigate();
  // When acting on behalf of a BP (active firm), create under that firm and
  // scope its practitioner picker to it.
  const { firm, actingFirmId } = useFirmContext();
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  // Pre-select the user's default practitioner (a firm practitioner) as the
  // required primary — §8 settings wiring. Blank when the user has none, or when
  // acting on behalf of another firm (the default belongs to the user's own firm
  // and wouldn't appear in the target firm's practitioner list).
  const userDefaultPractitionerId = useAuthStore((s) => s.user?.default_practitioner ?? "");
  const defaultPractitionerId = actingFirmId ? "" : userDefaultPractitionerId;
  // The "Refer by" affiliate tag is an NSI house-book concept — shown only when
  // creating for the house org itself (not when acting on behalf of a BP).
  const ownFirmOrgType = useAuthStore((s) => s.user?.firm?.org_type);
  const isHouse = !actingFirmId && ownFirmOrgType === "house";
  // "Skip onboarding" bypasses the New-Arrival pipeline — only a Super Admin or
  // the NSI house Country Director may do it (never a Business Practitioner).
  const showSkipOnboarding = useAuthStore((s) => canSkipOnboarding(s.user));
  const initialValues = useMemo<AddClientValues>(
    () => ({ ...baseInitialValues, practitioners: [defaultPractitionerId], assignedMembers: [] }),
    [defaultPractitionerId],
  );

  // Load the firm's practitioners for the assignment dropdowns — scoped to the
  // target firm when acting on behalf of a BP. For a BP client we also pull the
  // house CAP practitioners (NSI signature authority) so an unlicensed BP can
  // borrow one as the client's practitioner.
  useEffect(() => {
    let active = true;
    listPractitioners(firm, false, Boolean(actingFirmId))
      .then((list) => active && setPractitioners(list))
      .catch(() => active && toast.error("Couldn't load practitioners."));
    // Team members for the optional assignment picker, scoped to the same firm
    // as the practitioner picker — so acting on behalf of a BP lists that BP's
    // members. For a BP client we also request the house (NSI) team: the server
    // adds them only for a Country-Director-or-above house caller, so a house CD
    // can assign an NSI member to the BP's client. A load failure just leaves the
    // picker empty (assignment is optional).
    listMembers(firm, Boolean(actingFirmId))
      .then((list) => active && setMembers(list.filter((m) => m.status === "active")))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [firm]);

  async function handleSubmit(
    values: AddClientValues,
    { setErrors }: FormikHelpers<AddClientValues>,
  ) {
    // Never console.log values — they contain SSN/ITIN/EIN.
    try {
      await createClient(toPayload(values, isHouse), firm);
      toast.success("Client added");
      // The active firm persists in the store, so /clients stays in that
      // workspace when acting on behalf of a BP.
      navigate("/clients");
    } catch (err) {
      // Map server field errors back onto their inputs. `practitioner_ids` and
      // (in Business mode) `email` don't match their form field name 1:1.
      const fieldMap: Record<string, string> = { practitioner_ids: "practitioners" };
      if (values.clientType === "Business") fieldMap.email = "businessEmail";
      toast.error(
        applyApiErrors(err, { setErrors }, { fieldMap, fallback: "Couldn't add client." }),
      );
    }
  }

  return (
    <Formik<AddClientValues>
      initialValues={initialValues}
      validationSchema={addClientSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setFieldValue, errors, submitCount }) => {
        const isIndividual = values.clientType === "Individual";
        const primaryError =
          submitCount > 0 && typeof errors.practitioners === "string"
            ? errors.practitioners
            : undefined;

        // Practitioners already picked in other slots — filtered out so the same
        // person can't be assigned twice.
        const optionsFor = (slot: number) => {
          const takenElsewhere = new Set(values.practitioners.filter((p, i) => p && i !== slot));
          return practitioners.filter((p) => !takenElsewhere.has(p.id));
        };

        // The optional team-member picker is scoped to the primary practitioner's
        // own team — members whose default practitioner is the selected primary —
        // falling back to the whole firm when none are linked (or none picked yet).
        const primaryId = values.practitioners[0] || "";
        const teamFor = (practitionerId: string) => {
          const linked = practitionerId
            ? members.filter((m) => m.defaultPractitionerId === practitionerId)
            : [];
          return linked.length ? linked : members;
        };
        const teamOptions = teamFor(primaryId);

        const setPractitioner = (slot: number, value: string) => {
          setFieldValue(`practitioners.${slot}`, value);
          // Changing the primary re-scopes the team options; drop any now-invalid
          // selection so submitted ids can't reference an out-of-scope member.
          if (slot === 0) {
            const allowed = new Set(teamFor(value).map((m) => m.id));
            setFieldValue(
              "assignedMembers",
              values.assignedMembers.filter((id) => allowed.has(id)),
            );
          }
        };

        const addPractitioner = () => setFieldValue("practitioners", [...values.practitioners, ""]);

        const removePractitioner = (slot: number) =>
          setFieldValue(
            "practitioners",
            values.practitioners.filter((_, i) => i !== slot),
          );

        const requirements = isIndividual
          ? [
              { label: "First name", done: !!values.firstName },
              { label: "Last name", done: !!values.lastName },
              { label: "SSN/ITIN", done: !!values.ssnTin },
              { label: "Primary practitioner", done: !!values.practitioners[0] },
            ]
          : [
              { label: "Business name", done: !!values.businessName },
              { label: "EIN", done: !!values.ein },
              { label: "Primary practitioner", done: !!values.practitioners[0] },
            ];

        return (
          <Form>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
              {/* Main column */}
              <div className="flex flex-col gap-6 xl:col-span-8">
                {/* Client type — selectable tiles instead of a bare radio row */}
                <SectionCard icon={Users} title="Type of Client">
                  <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
                    {clientTypes.map(({ value, icon: Icon, title, hint }) => {
                      const active = values.clientType === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={active}
                          onClick={() => setFieldValue("clientType", value)}
                          className={cn(
                            "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                            active
                              ? "border-primary bg-primary/5 ring-primary/20 ring-2"
                              : "border-border hover:border-primary/40 hover:bg-muted/40",
                          )}
                        >
                          <span
                            className={cn(
                              "flex size-10 shrink-0 items-center justify-center rounded-lg",
                              active
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            <Icon className="size-5" />
                          </span>
                          <span className="grid gap-0.5">
                            <span className="text-foreground font-semibold">{title}</span>
                            <span className="text-muted-foreground text-sm">{hint}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </SectionCard>

                {/* Identity fields — swapped by client type */}
                <SectionCard icon={UserRound} title="Client Details">
                  {isIndividual ? (
                    <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
                      <FormField
                        name="firstName"
                        label="First Name"
                        labelClassName={FIELD_LABEL}
                        placeholder="John"
                      />
                      <FormField
                        name="middleInitial"
                        label="Middle Initial"
                        labelClassName={FIELD_LABEL}
                        maxLength={1}
                      />
                      <FormField
                        name="lastName"
                        label="Last Name"
                        labelClassName={FIELD_LABEL}
                        placeholder="Smith"
                      />
                      <SsnField name="ssnTin" label="SSN/ITIN" labelClassName={FIELD_LABEL} />
                      <div className="md:col-span-2">
                        <FormField
                          name="email"
                          label="Email"
                          labelClassName={FIELD_LABEL}
                          type="email"
                          placeholder="john@example.com"
                        />
                      </div>
                      <FormField
                        name="country"
                        label="Country"
                        labelClassName={FIELD_LABEL}
                        maxLength={2}
                        placeholder="US"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <FormField
                          name="businessName"
                          label="Business Name"
                          labelClassName={FIELD_LABEL}
                          placeholder="Acme LLC"
                        />
                      </div>
                      <FormField
                        name="businessEmail"
                        label="Email"
                        labelClassName={FIELD_LABEL}
                        type="email"
                        placeholder="billing@acme.com"
                      />
                      <EinField name="ein" label="EIN" labelClassName={FIELD_LABEL} />
                      <div className="md:col-span-2">
                        <FormField
                          name="dba"
                          label="DBA"
                          labelClassName={FIELD_LABEL}
                          placeholder="Doing-business-as name"
                        />
                      </div>
                      <FormField
                        name="country"
                        label="Country"
                        labelClassName={FIELD_LABEL}
                        maxLength={2}
                        placeholder="US"
                      />
                    </div>
                  )}
                </SectionCard>

                {/* Assigned practitioners — primary required, add/remove extras */}
                <SectionCard icon={Users} title="Assigned Practitioners">
                  <div className="grid gap-5 p-6">
                    <div className="grid gap-2">
                      <Label className={FIELD_LABEL}>
                        Primary Practitioner <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={values.practitioners[0] || undefined}
                        onValueChange={(next) => setPractitioner(0, next)}
                      >
                        <SelectTrigger className="w-full" aria-invalid={Boolean(primaryError)}>
                          <SelectValue placeholder="Select the lead practitioner" />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsFor(0).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {primaryError && <p className="text-destructive text-sm">{primaryError}</p>}
                    </div>

                    {values.practitioners.length > 1 && (
                      <div className="grid gap-3">
                        <Label className={FIELD_LABEL}>Additional Practitioners</Label>
                        {values.practitioners.slice(1).map((value, i) => {
                          const slot = i + 1;
                          return (
                            <div className="flex items-center gap-2" key={slot}>
                              <span className="text-muted-foreground w-4 text-sm">{slot + 1}</span>
                              <Select
                                value={value || undefined}
                                onValueChange={(next) => setPractitioner(slot, next)}
                              >
                                <SelectTrigger className="flex-1">
                                  <SelectValue placeholder="Select a practitioner" />
                                </SelectTrigger>
                                <SelectContent>
                                  {optionsFor(slot).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Remove practitioner ${slot + 1}`}
                                onClick={() => removePractitioner(slot)}
                              >
                                <X className="size-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addPractitioner}
                        disabled={values.practitioners.length >= MAX_PRACTITIONERS}
                      >
                        <Plus className="size-4" /> Add practitioner
                      </Button>
                    </div>
                  </div>
                </SectionCard>

                {/* Team-member assignment — optional; drives per-member client
                    visibility for "assigned only" accounts */}
                <SectionCard icon={Users} title="Assigned Team Members">
                  <div className="grid gap-3 p-6">
                    <Label className={FIELD_LABEL}>Team Members (optional)</Label>
                    <div>
                      <MemberMultiSelect
                        members={teamOptions}
                        selected={values.assignedMembers}
                        onChange={(ids) => setFieldValue("assignedMembers", ids)}
                        triggerSize="default"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Members set to "Show only assigned clients" will see this client only if
                      assigned here.
                    </p>
                  </div>
                </SectionCard>

                {/* Refer by — affiliate tag (NSI house book only). The affiliate
                    earns the commission % on this client's invoices. */}
                {isHouse && (
                  <SectionCard icon={Handshake} title="Refer by">
                    <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                      <FormField
                        name="referrerName"
                        label="Referrer name"
                        labelClassName={FIELD_LABEL}
                        placeholder="Affiliate / referrer"
                      />
                      <FormField
                        name="referralCommissionPercent"
                        label="Commission %"
                        labelClassName={FIELD_LABEL}
                        type="number"
                        placeholder="5"
                      />
                    </div>
                  </SectionCard>
                )}
              </div>

              {/* Sidebar — live "required fields" checklist */}
              <div className="xl:col-span-4">
                <Card className="sticky top-[144px] gap-3 p-5">
                  <h4 className="text-foreground text-sm font-bold">Required to add</h4>
                  <ul className="grid gap-2.5">
                    {requirements.map((req) => (
                      <li key={req.label} className="flex items-center gap-2 text-sm">
                        {req.done ? (
                          <Check className="text-success size-4" />
                        ) : (
                          <Circle className="text-muted-foreground/50 size-4" />
                        )}
                        <span className={req.done ? "text-foreground" : "text-muted-foreground"}>
                          {req.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground border-t pt-3 text-xs">
                    After adding, you'll land on the client's profile to finish their details.
                  </p>
                  {showSkipOnboarding && (
                    <div className="border-t pt-3">
                      <label className="flex items-start gap-2.5 text-sm">
                        <Checkbox
                          aria-label="Skip onboarding"
                          checked={values.skipOnboarding}
                          onCheckedChange={(next) =>
                            setFieldValue("skipOnboarding", next === true)
                          }
                          className="mt-0.5"
                        />
                        <span className="grid gap-0.5">
                          <span className="text-foreground font-medium">Skip onboarding</span>
                          <span className="text-muted-foreground text-xs">
                            Add as an active client right away, bypassing the New Arrivals queue.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </Card>
              </div>
            </div>

            {/* Sticky action bar — same -mx-6 -mb-6 trick as the client profile page
                so it spans flush to the AppLayout main's edges. */}
            <div className="bg-card sticky bottom-0 z-10 -mx-6 mt-6 -mb-6 flex items-center justify-end gap-3 border-t px-6 py-4">
              <Button type="button" variant="ghost" onClick={() => navigate("/clients")}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Add Client"}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
